/**
 * DeletePeriodConfirmDialog Component
 *
 * Confirmation dialog for deleting an OKR period and all its cascade data.
 * Fetches cascade counts on open and requires type-to-confirm safety.
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    DeleteForever,
    Warning,
    FlagCircle,
    Groups,
    Person,
    TaskAlt,
    StickyNote2,
} from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { okrApi } from '../../../services/okrApi';

interface CascadeCounts {
    period_name: string;
    orgObjectives: number;
    teamObjectives: number;
    individualObjectives: number;
    milestones: number;
    notes: number;
}

export const DeletePeriodConfirmDialog: React.FC = () => {
    const __muiTheme = useTheme();
    const isMobile = useMediaQuery(__muiTheme.breakpoints.down("sm"));
    const { isDarkMode, data, dialogs } = useOKRDashboardContext();
    const { setSuccess, setError, refetchData } = data;
    const {
        dialogs: dialogState,
        formState,
        closeDeletePeriodDialog,
    } = dialogs;

    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [counts, setCounts] = useState<CascadeCounts | null>(null);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [confirmText, setConfirmText] = useState('');

    const periodId = formState.deletePeriodId;
    const periodName = formState.deletePeriodName;

    // Fetch cascade counts when dialog opens
    useEffect(() => {
        if (dialogState.deletePeriodDialog && periodId) {
            setLoading(true);
            setFetchError(null);
            setCounts(null);
            setConfirmText('');

            okrApi.getPeriodCascadeCounts(periodId)
                .then((response) => {
                    setCounts(response);
                })
                .catch((err: any) => {
                    setFetchError(err.message || 'Failed to load period data');
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [dialogState.deletePeriodDialog, periodId]);

    const totalItems = counts
        ? counts.orgObjectives + counts.teamObjectives + counts.individualObjectives + counts.milestones + counts.notes
        : 0;

    const isConfirmed = confirmText === periodName;

    const handleDelete = async () => {
        if (!periodId || !isConfirmed) return;

        try {
            setDeleting(true);
            await okrApi.deletePeriod(periodId);
            setSuccess(`Period "${periodName}" and all its data deleted successfully`);
            closeDeletePeriodDialog();
            refetchData();
        } catch (err: any) {
            setError(err.message || 'Failed to delete period');
        } finally {
            setDeleting(false);
        }
    };

    const handleClose = () => {
        setConfirmText('');
        setCounts(null);
        setFetchError(null);
        closeDeletePeriodDialog();
    };

    return (
        <Dialog
            open={dialogState.deletePeriodDialog}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <DeleteForever sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444' }}>
                        Delete Period
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Loading State */}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={36} sx={{ color: '#ef4444' }} />
                        </Box>
                    )}

                    {/* Error from API (period is active or unlocked) */}
                    {fetchError && (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                            {fetchError}
                        </Alert>
                    )}

                    {/* Cascade Counts Preview */}
                    {counts && !fetchError && (
                        <>
                            <Alert
                                severity="warning"
                                icon={<Warning />}
                                sx={{ borderRadius: 2 }}
                            >
                                This will permanently delete <strong>"{periodName}"</strong> and
                                all associated data. This action cannot be undone.
                            </Alert>

                            {totalItems > 0 && (
                                <Box
                                    sx={{
                                        bgcolor: isDarkMode ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.04)',
                                        borderRadius: 2,
                                        border: `1px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)'}`,
                                        p: 1,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 700, color: '#ef4444', px: 2, pt: 1 }}
                                    >
                                        Data that will be deleted:
                                    </Typography>
                                    <List dense>
                                        {counts.orgObjectives > 0 && (
                                            <ListItem>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <FlagCircle sx={{ color: '#ef4444', fontSize: 20 }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={`${counts.orgObjectives} Org Objective${counts.orgObjectives > 1 ? 's' : ''}`}
                                                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
                                                />
                                            </ListItem>
                                        )}
                                        {counts.teamObjectives > 0 && (
                                            <ListItem>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <Groups sx={{ color: '#f97316', fontSize: 20 }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={`${counts.teamObjectives} Team Objective${counts.teamObjectives > 1 ? 's' : ''}`}
                                                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
                                                />
                                            </ListItem>
                                        )}
                                        {counts.individualObjectives > 0 && (
                                            <ListItem>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <Person sx={{ color: '#eab308', fontSize: 20 }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={`${counts.individualObjectives} Individual Objective${counts.individualObjectives > 1 ? 's' : ''}`}
                                                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
                                                />
                                            </ListItem>
                                        )}
                                        {counts.milestones > 0 && (
                                            <ListItem>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <TaskAlt sx={{ color: '#22c55e', fontSize: 20 }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={`${counts.milestones} Milestone${counts.milestones > 1 ? 's' : ''}`}
                                                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
                                                />
                                            </ListItem>
                                        )}
                                        {counts.notes > 0 && (
                                            <ListItem>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <StickyNote2 sx={{ color: '#8b5cf6', fontSize: 20 }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={`${counts.notes} Note${counts.notes > 1 ? 's' : ''}`}
                                                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
                                                />
                                            </ListItem>
                                        )}
                                    </List>
                                </Box>
                            )}

                            {totalItems === 0 && (
                                <Typography
                                    variant="body2"
                                    sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                                >
                                    This period has no associated data. Only the period itself will be deleted.
                                </Typography>
                            )}

                            {/* Type-to-confirm */}
                            <Box sx={{ mt: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 1, fontWeight: 600 }}
                                >
                                    Type <strong style={{ color: '#ef4444' }}>{periodName}</strong> to confirm:
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    placeholder={periodName}
                                    autoComplete="off"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isConfirmed ? '#ef4444' : undefined,
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1 }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        fontWeight: 600,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleDelete}
                    disabled={!isConfirmed || deleting || loading || !!fetchError}
                    sx={{
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                        '&:hover': {
                            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                        },
                    }}
                >
                    {deleting
                        ? 'Deleting...'
                        : totalItems > 0
                            ? `Delete Period (${totalItems} items)`
                            : 'Delete Period'
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeletePeriodConfirmDialog;
